import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';
import { errorMessage } from '../lib/api';
import { formatMoney, formatShortDate, parseMoney } from '../lib/format';
import type { Paginated } from '../types';
import {
  Button,
  ColorPalette,
  ConfirmDialog,
  ErrorNote,
  Field,
  Loading,
  Modal,
  Toggle,
  inputClass,
} from './ui';

/** Um campo do formulário de cadastro simples. */
export interface CatalogFormField {
  key: string;
  label: string;
  kind: 'text' | 'textarea' | 'money' | 'color' | 'toggle';
  required?: boolean;
  placeholder?: string;
  hint?: string;
  /** Valor da criação (ex.: 1ª cor pré-marcada na tag, nenhuma no tipo de atividade). */
  initial?: unknown;
}

export interface CatalogColumn<T> {
  header: string;
  render: (item: T) => ReactNode;
  className?: string;
}

interface CatalogApi<T> {
  list: (params: { search?: string }) => Promise<Paginated<T>>;
  create: (payload: Partial<T>) => Promise<T>;
  update: (id: string, payload: Partial<T>) => Promise<T>;
  remove: (id: string) => Promise<void>;
  removeMany: (ids: string[]) => Promise<{ deleted: number }>;
}

interface CatalogTableProps<T> {
  title: string;
  subtitle: string;
  /** "tag" / "tags" — usados nos textos de confirmação. */
  noun: { one: string; many: string };
  queryKey: string;
  api: CatalogApi<T>;
  columns: CatalogColumn<T>[];
  fields: CatalogFormField[];
  labelOf: (item: T) => string;
  canWrite: boolean;
}

type Row = { id: string; createdAt?: string };

/**
 * O "padrão comum dos cadastros simples" do doc 01: título + Criar, busca com
 * contador, tabela com seleção, data de criação, ✏ e 🗑. Excluir vai para a
 * Lixeira e sempre pede confirmação — uma a uma ou em massa.
 */
export function CatalogTable<T extends Row>({
  title,
  subtitle,
  noun,
  queryKey,
  api,
  columns,
  fields,
  labelOf,
  canWrite,
}: CatalogTableProps<T>) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [editing, setEditing] = useState<T | 'new' | null>(null);
  const [confirm, setConfirm] = useState<{ ids: string[]; label: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: [queryKey, search],
    queryFn: () => api.list({ search: search || undefined }),
  });
  const rows = query.data?.data ?? [];
  const invalidate = () => queryClient.invalidateQueries({ queryKey: [queryKey] });

  const removal = useMutation({
    mutationFn: (ids: string[]) =>
      ids.length === 1 ? api.remove(ids[0]) : api.removeMany(ids).then(() => undefined),
    onSuccess: () => {
      setConfirm(null);
      setSelected([]);
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['trash'] });
    },
    onError: (mutationError) =>
      setError(errorMessage(mutationError, 'Não foi possível excluir')),
  });

  const allSelected = rows.length > 0 && rows.every((row) => selected.includes(row.id));
  const toggle = (id: string) =>
    setSelected((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );

  return (
    <section className="rounded-xl border border-slate-200 bg-white">
      <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <p className="text-xs text-slate-500">{subtitle}</p>
        </div>
        {canWrite && (
          <Button variant="primary" onClick={() => setEditing('new')}>
            Criar
          </Button>
        )}
      </header>

      <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-3">
        <input
          className={`${inputClass} mt-0 max-w-xs`}
          placeholder="Pesquisar..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <span className="text-xs text-slate-500">
          {selected.length > 0
            ? `${selected.length} selecionado(s)`
            : `${query.data?.count ?? 0} resultado(s)`}
        </span>
        {canWrite && selected.length > 0 && (
          <Button
            variant="danger"
            onClick={() => {
              setError(null);
              setConfirm({ ids: selected, label: `${selected.length} ${noun.many}` });
            }}
          >
            Excluir selecionados
          </Button>
        )}
      </div>

      {query.isLoading ? (
        <Loading />
      ) : rows.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-slate-400">
          {search ? 'Nada encontrado.' : `Nenhum(a) ${noun.one} cadastrado(a).`}
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-slate-400">
            <tr>
              {canWrite && (
                <th className="w-10 px-5 py-2">
                  <input
                    type="checkbox"
                    aria-label="Selecionar todos"
                    checked={allSelected}
                    onChange={() => setSelected(allSelected ? [] : rows.map((row) => row.id))}
                  />
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={column.header}
                  className={`px-3 py-2 font-medium ${column.className ?? ''}`}
                >
                  {column.header}
                </th>
              ))}
              <th className="px-3 py-2 font-medium">Data de criação</th>
              {canWrite && <th className="w-24 px-5 py-2" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row) => (
              <tr key={row.id} className="hover:bg-slate-50">
                {canWrite && (
                  <td className="px-5 py-2.5">
                    <input
                      type="checkbox"
                      aria-label={`Selecionar ${labelOf(row)}`}
                      checked={selected.includes(row.id)}
                      onChange={() => toggle(row.id)}
                    />
                  </td>
                )}
                {columns.map((column) => (
                  <td key={column.header} className={`px-3 py-2.5 ${column.className ?? ''}`}>
                    {column.render(row)}
                  </td>
                ))}
                <td className="px-3 py-2.5 text-slate-500">{formatShortDate(row.createdAt)}</td>
                {canWrite && (
                  <td className="px-5 py-2.5 text-right">
                    <button
                      type="button"
                      className="mr-3 text-xs text-slate-500 hover:text-slate-900"
                      onClick={() => setEditing(row)}
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      className="text-xs text-slate-400 hover:text-red-600"
                      onClick={() => {
                        setError(null);
                        setConfirm({ ids: [row.id], label: `"${labelOf(row)}"` });
                      }}
                    >
                      Excluir
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <CatalogFormModal
          title={editing === 'new' ? `Criar ${noun.one}` : `Editar ${noun.one}`}
          fields={fields}
          item={editing === 'new' ? null : (editing as unknown as Record<string, unknown>)}
          onClose={() => setEditing(null)}
          onSubmit={async (payload) => {
            if (editing === 'new') await api.create(payload as Partial<T>);
            else await api.update(editing.id, payload as Partial<T>);
            invalidate();
            setEditing(null);
          }}
        />
      )}

      {confirm && (
        <ConfirmDialog
          title={confirm.ids.length > 1 ? `Excluir ${confirm.label}?` : `Excluir ${noun.one}?`}
          message={
            <>
              <p>
                {confirm.ids.length > 1 ? (
                  <>
                    <strong>{confirm.label}</strong> vão para a Lixeira.
                  </>
                ) : (
                  <>
                    <strong>{confirm.label}</strong> vai para a Lixeira.
                  </>
                )}
              </p>
              <p className="mt-1 text-xs text-slate-400">
                Dá para restaurar em Configurações › Lixeira por 30 dias.
              </p>
            </>
          }
          confirmLabel="Excluir"
          danger
          busy={removal.isPending}
          error={error}
          onConfirm={() => removal.mutate(confirm.ids)}
          onClose={() => setConfirm(null)}
        />
      )}
    </section>
  );
}

/** Formulário em modal: "Confirmar" na criação, "Atualizar" na edição (como o DataCrazy). */
function CatalogFormModal({
  title,
  fields,
  item,
  onSubmit,
  onClose,
}: {
  title: string;
  fields: CatalogFormField[];
  item: Record<string, unknown> | null;
  onSubmit: (payload: Record<string, unknown>) => Promise<void>;
  onClose: () => void;
}) {
  const [values, setValues] = useState<Record<string, unknown>>(() =>
    Object.fromEntries(
      fields.map((field) => {
        const current = item ? item[field.key] : field.initial;
        if (field.kind === 'money') {
          return [
            field.key,
            current === undefined || current === null ? '' : formatMoney(Number(current)),
          ];
        }
        if (field.kind === 'toggle') return [field.key, Boolean(current)];
        if (field.kind === 'color') return [field.key, (current as string | null) ?? null];
        return [field.key, (current as string | null) ?? ''];
      }),
    ),
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        fields.map((field) => {
          const value = values[field.key];
          if (field.kind === 'money') return [field.key, parseMoney(String(value || '0'))];
          if (field.kind === 'text' || field.kind === 'textarea') {
            const text = String(value ?? '').trim();
            // Vazio na edição limpa o campo; na criação, só não manda.
            if (text === '' && !field.required) return [field.key, item ? null : undefined];
            return [field.key, text];
          }
          return [field.key, value ?? undefined];
        }),
      );
      await onSubmit(payload);
    } catch (submitError) {
      setError(errorMessage(submitError, 'Não foi possível salvar'));
    } finally {
      setSaving(false);
    }
  };

  const set = (key: string, value: unknown) =>
    setValues((current) => ({ ...current, [key]: value }));

  return (
    <Modal title={title} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {fields.map((field) => {
          if (field.kind === 'toggle') {
            return (
              <Toggle
                key={field.key}
                label={field.label}
                hint={field.hint}
                checked={Boolean(values[field.key])}
                onChange={(checked) => set(field.key, checked)}
              />
            );
          }
          if (field.kind === 'color') {
            return (
              <div key={field.key} className="text-sm">
                <span className="font-medium text-slate-700">{field.label}</span>
                <ColorPalette
                  value={(values[field.key] as string | null) ?? null}
                  onChange={(color) => set(field.key, color)}
                />
              </div>
            );
          }
          return (
            <Field
              key={field.key}
              label={`${field.label}${field.required ? ' *' : ''}`}
              hint={field.hint}
            >
              {field.kind === 'textarea' ? (
                <textarea
                  rows={3}
                  className={inputClass}
                  placeholder={field.placeholder}
                  value={String(values[field.key] ?? '')}
                  onChange={(event) => set(field.key, event.target.value)}
                />
              ) : (
                <input
                  required={field.required}
                  autoFocus={field === fields[0]}
                  className={inputClass}
                  placeholder={field.placeholder}
                  inputMode={field.kind === 'money' ? 'decimal' : undefined}
                  value={String(values[field.key] ?? '')}
                  onChange={(event) => set(field.key, event.target.value)}
                />
              )}
            </Field>
          );
        })}

        {error && <ErrorNote>{error}</ErrorNote>}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? 'Salvando…' : item ? 'Atualizar' : 'Confirmar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
